import React, { useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  AlertsByCustomerRow,
  AlertsByTypeRow,
  AlertsMetricInfo,
} from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";
import { AlertsDashboardStore } from "../../../../stores/dashboards/AlertsDashboardStore";
import {
  RedemptionEmptyState,
  RedemptionSectionLabel,
} from "../redemption/RedemptionSection";
import RedemptionHint from "../redemption/RedemptionHint";
import { AlertsPill } from "./AlertsDataTable";
import {
  ALERTS_EMPTY_VALUE,
  formatAlertsCount,
  formatAlertsPct,
} from "./alertsDashboardFormat";

interface BreakdownColumn<T> {
  key: keyof T & string;
  label: string;
  align: "left" | "right";
  numeric?: boolean;
  render?: (row: T) => React.ReactNode;
}

const headerCellSx = {
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  borderBottom: "1px solid",
  py: 1.1,
};

/** Client-side sort of a summary block (the server sends every row; no paging). */
function BreakdownTable<T extends { metricId: string }>({
  columns,
  rows,
  how,
  defaultSort,
  onRowClick,
}: {
  columns: BreakdownColumn<T>[];
  rows: T[];
  how: Record<string, AlertsMetricInfo> | undefined;
  defaultSort: keyof T & string;
  onRowClick: (row: T) => void;
}) {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const [sort, setSort] = useState<{
    key: keyof T & string;
    dir: "asc" | "desc";
  }>({
    key: defaultSort,
    dir: "desc",
  });

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.key] as unknown;
      const bv = b[sort.key] as unknown;
      // Nulls (e.g. read_pct with no recipients) always sink to the bottom.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort]);

  const toggleSort = (key: keyof T & string) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" },
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: colors.lightGray,
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    ...headerCellSx,
                    color: colors.greyTextColor,
                    bgcolor: colors.white,
                    borderColor: colors.lightGray,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <TableSortLabel
                      active={sort.key === column.key}
                      direction={sort.key === column.key ? sort.dir : "desc"}
                      onClick={() => toggleSort(column.key)}
                      sx={{
                        color: colors.greyTextColor,
                        "&.Mui-active": { color: colors.navyText },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                    <RedemptionHint
                      how={how?.[column.key] ?? null}
                      fontSize={12}
                    />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow
                key={row.metricId}
                hover
                tabIndex={0}
                onClick={() => onRowClick(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onRowClick(row);
                  }
                }}
                sx={{ cursor: "pointer" }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sx={{ borderColor: colors.lightGray }}
                  >
                    <CustomTypography
                      variant="body3"
                      fontWeight={column.numeric ? 600 : 500}
                      color={colors.navyText}
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {column.render
                        ? column.render(row)
                        : column.numeric
                          ? formatAlertsCount(
                              row[column.key] as unknown as number,
                            )
                          : String(row[column.key] ?? ALERTS_EMPTY_VALUE)}
                    </CustomTypography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

interface AlertsBreakdownProps {
  store: AlertsDashboardStore;
}

const BY_TYPE_COLUMNS: BreakdownColumn<AlertsByTypeRow>[] = [
  { key: "type_code", label: "Type", align: "left" },
  {
    key: "audience",
    label: "Audience",
    align: "left",
    render: (row) => <AlertsPill value={row.audience} />,
  },
  {
    key: "section_code",
    label: "Section",
    align: "left",
    render: (row) => row.section_label || row.section_code,
  },
  { key: "raised", label: "Raised", align: "right", numeric: true },
  { key: "open", label: "Open", align: "right", numeric: true },
  { key: "resolved", label: "Resolved", align: "right", numeric: true },
  { key: "informational", label: "Info", align: "right", numeric: true },
  { key: "recipients", label: "Recipients", align: "right", numeric: true },
  { key: "read", label: "Read", align: "right", numeric: true },
  {
    key: "read_pct",
    label: "Read %",
    align: "right",
    numeric: true,
    render: (row) => formatAlertsPct(row.read_pct),
  },
  { key: "push_sent", label: "Push sent", align: "right", numeric: true },
  { key: "push_failed", label: "Push failed", align: "right", numeric: true },
  {
    key: "email_delivered",
    label: "Email delivered",
    align: "right",
    numeric: true,
  },
  { key: "email_failed", label: "Email failed", align: "right", numeric: true },
];

const BY_CUSTOMER_COLUMNS: BreakdownColumn<AlertsByCustomerRow>[] = [
  {
    key: "customer_name",
    label: "Customer",
    align: "left",
    render: (row) => `${row.customer_name || "Customer"} (#${row.customer_id})`,
  },
  { key: "raised", label: "Raised", align: "right", numeric: true },
  { key: "open", label: "Open", align: "right", numeric: true },
  { key: "emails", label: "Emails", align: "right", numeric: true },
  { key: "emails_sent", label: "Sent", align: "right", numeric: true },
  {
    key: "emails_delivered",
    label: "Delivered",
    align: "right",
    numeric: true,
  },
  { key: "emails_bounced", label: "Bounced", align: "right", numeric: true },
  { key: "emails_failed", label: "Failed", align: "right", numeric: true },
];

const AlertsBreakdownTables: React.FC<AlertsBreakdownProps> = ({ store }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const summary = store.summaryResult;

  if (!summary || store.summaryError) {
    return null;
  }

  const byType = summary.data.by_type;
  const byCustomer = summary.data.by_customer;
  const showCustomers = summary.meta.audience !== "ADMIN";

  return (
    <Box sx={{ mb: 4 }}>
      <RedemptionSectionLabel
        label="By alert type"
        how={byType?.how?.raised ?? null}
      />
      {byType && byType.rows.length > 0 ? (
        <BreakdownTable
          columns={BY_TYPE_COLUMNS}
          rows={byType.rows}
          how={byType.how}
          defaultSort="raised"
          onRowClick={(row) =>
            void store.openDrilldown(
              row.metricId,
              row.type_code,
              byType.how?.raised ?? null,
              "type_row_click",
            )
          }
        />
      ) : (
        <RedemptionEmptyState message="No alert types for this filter." />
      )}

      {showCustomers ? (
        <>
          <RedemptionSectionLabel
            label="By customer"
            how={byCustomer?.how?.raised ?? null}
          />
          {byCustomer && byCustomer.rows.length > 0 ? (
            <>
              <BreakdownTable
                columns={BY_CUSTOMER_COLUMNS}
                rows={byCustomer.rows}
                how={byCustomer.how}
                defaultSort="raised"
                onRowClick={(row) =>
                  void store.openDrilldown(
                    row.metricId,
                    row.customer_name || `Customer #${row.customer_id}`,
                    byCustomer.how?.raised ?? null,
                    "customer_row_click",
                  )
                }
              />
              {byCustomer.truncated ? (
                <CustomTypography
                  variant="body4"
                  color={colors.greyTextColor}
                  sx={{ mt: 1 }}
                >
                  Showing the top {formatAlertsCount(byCustomer.rows.length)} of{" "}
                  {formatAlertsCount(byCustomer.total)} customers by alerts
                  raised.
                </CustomTypography>
              ) : null}
            </>
          ) : (
            <RedemptionEmptyState message="No client alerts for this filter." />
          )}
        </>
      ) : null}
    </Box>
  );
};

export default observer(AlertsBreakdownTables);
